import React, { useEffect, useState, useMemo } from "react";
import api, { todayISO } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, PageHeader, Empty } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, GraduationCap, MessageSquare, TrendingUp, BookOpen } from "lucide-react";

const TYPES = ["SOP Training", "Skill Session", "Feedback", "Improvement Plan"];
const STATUS = ["Assigned", "In Progress", "Completed"];
const typeIcon = { "SOP Training": BookOpen, "Skill Session": GraduationCap, "Feedback": MessageSquare, "Improvement Plan": TrendingUp };
const statusColor = (s) => s === "Completed" ? "bg-emerald-600" : s === "In Progress" ? "bg-[#C9A24B] text-[#0b1120]" : "bg-slate-600";

export default function Training() {
  const { technicians, filters, user } = useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const canManage = ["super_admin", "ot_head", "branch_coordinator"].includes(user?.app_role);

  const load = () => {
    setLoading(true);
    const params = {};
    if (filters.branch !== "all") params.branch = filters.branch;
    if (filters.technicianId !== "all") params.technician_id = filters.technicianId;
    api.get("/training", { params }).then(({ data }) => setRows(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const tname = (id) => technicians.find((t) => t.employee_id === id)?.name || id;
  const techs = useMemo(() => technicians.filter((t) => t.status === "Active"), [technicians]);
  const filtered = tab === "all" ? rows : rows.filter((r) => r.type === tab);
  const blank = { date: todayISO(), technician_id: "", branch: "", type: "SOP Training", title: "", sop_reference: "", trainer: user?.name || "", status: "Assigned", score: null, feedback: "", improvement_action: "", remarks: "" };

  const openNew = () => { setEdit({ ...blank }); setOpen(true); };
  const openEdit = (r) => { setEdit({ ...r }); setOpen(true); };
  const save = async () => {
    const t = technicians.find((x) => x.employee_id === edit.technician_id);
    if (!t || !edit.title) { toast.error("Technician & title required"); return; }
    const payload = { ...edit, branch: t.branch, score: edit.score === "" ? null : edit.score };
    try {
      if (edit.id) await api.put(`/training/${edit.id}`, payload); else await api.post("/training", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error("Save failed"); }
  };
  const remove = async (id) => { await api.delete(`/training/${id}`); toast.success("Deleted"); load(); };
  const inp = "bg-[#0f172a] border border-white/10 rounded-md px-3 py-2 text-sm w-full focus:ring-2 focus:ring-[#C9A24B] focus:outline-none";

  return (
    <div className="animate-fade-up" data-testid="training-page">
      <PageHeader title="Training, Feedback & Growth" subtitle="Individual SOP learning, skill sessions, feedback and improvement plans — equip the team to keep improving."
        action={canManage && <button data-testid="add-training-btn" onClick={openNew} className="flex items-center gap-2 bg-[#C9A24B] text-[#0b1120] font-semibold px-4 py-2 rounded-md hover:brightness-110 transition-all"><Plus size={16} /> New Record</button>} />

      <div className="flex flex-wrap gap-2 mb-5">
        {["all", ...TYPES].map((t) => (
          <button key={t} data-testid={`training-tab-${t}`} onClick={() => setTab(t)}
            className={`text-sm px-4 py-1.5 rounded-full border transition-colors ${tab === t ? "bg-[#C9A24B] text-[#0b1120] border-[#C9A24B] font-semibold" : "border-white/10 text-slate-300 hover:bg-white/10"}`}>
            {t === "all" ? "All" : t}
          </button>
        ))}
      </div>

      {loading ? <Empty text="Loading…" /> : filtered.length === 0 ? <Empty text="No records yet." /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((r) => {
            const Icon = typeIcon[r.type] || BookOpen;
            return (
              <Card key={r.id} className="p-4 hover:-translate-y-1 transition-transform" data-testid={`training-card-${r.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2"><Icon size={18} className="text-[#C9A24B]" /><span className="text-[10px] uppercase tracking-wider text-slate-400">{r.type}</span></div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full text-white ${statusColor(r.status)}`}>{r.status}</span>
                </div>
                <h4 className="font-medium text-slate-100">{r.title}</h4>
                <p className="text-xs text-slate-400 mt-1">{tname(r.technician_id)} · {r.branch} · {r.date}</p>
                {r.sop_reference && <p className="text-xs text-[#C9A24B] mt-1">Ref: {r.sop_reference}</p>}
                {r.score != null && <p className="text-xs text-emerald-400 mt-1">Score: {r.score}%</p>}
                {r.feedback && <p className="text-xs text-slate-300 mt-2 italic">"{r.feedback}"</p>}
                {r.improvement_action && <p className="text-xs text-slate-300 mt-1">Action: {r.improvement_action}</p>}
                {canManage && <button onClick={() => openEdit(r)} className="mt-3 text-xs text-slate-400 hover:text-[#C9A24B] flex items-center gap-1" data-testid={`edit-training-${r.id}`}><Pencil size={13} /> Edit</button>}
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F172A] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle>{edit?.id ? "Edit" : "New"} Training / Feedback</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-400">Date</label><input type="date" className={inp} value={edit.date} onChange={(e) => setEdit({ ...edit, date: e.target.value })} /></div>
              <div><label className="text-xs text-slate-400">Technician</label><select data-testid="training-technician" className={inp} value={edit.technician_id} onChange={(e) => setEdit({ ...edit, technician_id: e.target.value })}><option value="">Select…</option>{techs.map((t) => <option key={t.employee_id} value={t.employee_id}>{t.name}</option>)}</select></div>
              <div><label className="text-xs text-slate-400">Type</label><select className={inp} value={edit.type} onChange={(e) => setEdit({ ...edit, type: e.target.value })}>{TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
              <div><label className="text-xs text-slate-400">Status</label><select className={inp} value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })}>{STATUS.map((t) => <option key={t}>{t}</option>)}</select></div>
              <div className="col-span-2"><label className="text-xs text-slate-400">Title</label><input data-testid="training-title" className={inp} value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} /></div>
              <div><label className="text-xs text-slate-400">SOP Reference</label><input className={inp} value={edit.sop_reference} onChange={(e) => setEdit({ ...edit, sop_reference: e.target.value })} /></div>
              <div><label className="text-xs text-slate-400">Score (%)</label><input type="number" className={inp} value={edit.score ?? ""} onChange={(e) => setEdit({ ...edit, score: e.target.value === "" ? null : parseFloat(e.target.value) })} /></div>
              <div className="col-span-2"><label className="text-xs text-slate-400">Feedback</label><input className={inp} value={edit.feedback} onChange={(e) => setEdit({ ...edit, feedback: e.target.value })} /></div>
              <div className="col-span-2"><label className="text-xs text-slate-400">Improvement Action</label><input className={inp} value={edit.improvement_action} onChange={(e) => setEdit({ ...edit, improvement_action: e.target.value })} /></div>
              <div className="col-span-2 flex gap-2 mt-1">
                <button data-testid="save-training-btn" onClick={save} className="flex-1 bg-emerald-600 text-white font-semibold py-2 rounded-md hover:brightness-110 transition-all">Save</button>
                {edit.id && <button onClick={() => { remove(edit.id); setOpen(false); }} className="px-4 bg-[#DC2626] text-white rounded-md">Delete</button>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
